import React, { useId, useState } from 'react';

import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { ButtonBase, ListItemText, Menu, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { paneTokens } from './paneTokens';

const MAX_FOLLOW_UPS = 3;
const FOLLOW_UP_LABELS: Record<string, string> = {
  'Why did it move?': 'Explain change',
  'Show the numbers behind this': 'Show data',
  'Add this as a card': 'Add card',
  'Compare with the previous period': 'Compare periods',
  'Break this down further': 'Break it down',
};

/**
 * Two or three next questions after a settled answer. They are prompts the person can send,
 * not claims about the data, so they stay generic to the scope rather than guessing at figures.
 */
export function suggestedFollowUps({
  cardTitle,
  lensScoped,
}: {
  cardTitle?: string;
  lensScoped: boolean;
}): Array<string> {
  if (cardTitle) {
    return ['Why did it move?', 'Show the numbers behind this', 'Add this as a card'].slice(0, MAX_FOLLOW_UPS);
  }
  if (lensScoped) {
    return ['Compare with the previous period', 'Break this down further', 'Add this as a card'].slice(
      0,
      MAX_FOLLOW_UPS,
    );
  }
  return [];
}

interface FollowUpsProps {
  questions: ReadonlyArray<string>;
  onPick: (question: string) => void;
}

export default function FollowUps({ questions, onPick }: FollowUpsProps) {
  const theme = useTheme();
  const tokens = paneTokens(theme);
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <ButtonBase
        aria-controls={open ? menuId : undefined}
        aria-expanded={open}
        aria-haspopup="menu"
        data-testid="ui-stream-follow-ups"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          gap: 0.25,
          px: 0.75,
          py: 0.25,
          minHeight: 28,
          fontSize: 12.5,
          lineHeight: 1.3,
          borderRadius: 0.75,
          color: 'text.secondary',
          '&:hover': { bgcolor: 'action.hover', color: tokens.accent },
          '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 2 },
        }}
      >
        Examples
        <KeyboardArrowUpIcon sx={{ fontSize: 16, transform: open ? 'rotate(180deg)' : undefined }} />
      </ButtonBase>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        id={menuId}
        MenuListProps={{ 'aria-label': 'Example follow-up questions', dense: true }}
        onClose={() => setAnchorEl(null)}
        open={open}
        PaperProps={{
          sx: { border: `1px solid ${tokens.hairline}`, borderRadius: 1, maxWidth: 'calc(100vw - 32px)', width: 300 },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {questions.map((question) => {
          const label = FOLLOW_UP_LABELS[question] ?? question;
          return (
            <MenuItem
              key={question}
              onClick={() => {
                setAnchorEl(null);
                onPick(question);
              }}
              sx={{ px: 1.5, py: 1, whiteSpace: 'normal' }}
            >
              <ListItemText
                primary={label}
                secondary={label === question ? undefined : question}
                primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }}
                secondaryTypographyProps={{ fontSize: 12.5, lineHeight: 1.4 }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
